import { Button, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { InvoiceManager } from '../../services/api/InvoiceManager'
import { InvoiceDto, InvoicePaymentType } from '../../services/api/sharedTypes/Invoice'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import ScreenLoadingPlaceholder from '../ScreenLoadingPlaceholder'
import HappyTimesLogo from '../../assets/images/HappyTimesLogo.png'
import { getNumberRoundedInFormattedString } from '../../utils/global-utils'
import { convertInvoicePaymentTypeToString } from '../../utils/invoice-utils'

const InvoiceDetail: React.FC = () => {
    const { id } = useParams()
    const [loadingPageData, setLoadingPageData] = useState(false)
    const [invoice, setInvoice] = useState<InvoiceDto>()
    const [isExportButtonPressed, setIsExportButtonPressed] = useState(false)

    const theme = useTheme()
    const dispatch = useAppDispatch()

    const isOnPrintScreen = useMediaQuery(theme.breakpoints.down('md'), {
        defaultMatches: true,
    })

    useEffect(() => {
        const fetchData = async () => {
            setLoadingPageData(true)
            try {
                if (!id) throw new Error('Faktura nenalezena')

                const invoiceRes = await InvoiceManager.getManager().getInvoiceAsync(id)
                if (invoiceRes) {
                    setInvoice(invoiceRes)
                }
            } catch (error) {
                setLoadingPageData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingPageData(false)
        }

        fetchData()
    }, [setLoadingPageData])

    useEffect(() => {
        if (isExportButtonPressed) {
            document.title = `${invoice?.invoiceNumber} - ${invoice?.client?.name}`
            window.print()
            setIsExportButtonPressed(false)
            document.title = 'Happy CRM'
        }
    }, [isExportButtonPressed])

    if (loadingPageData) return <ScreenLoadingPlaceholder />

    if (!invoice) return <Typography variant='h4'>Faktura nenalezena</Typography>

    const isInvoiceTypeSelected = (invoice.paymentType !== undefined && invoice.paymentType !== InvoicePaymentType.NotSet)

    return (
        <div style={{ backgroundColor: theme.palette.background.default }}>
            <div
                style={{
                    display: !isExportButtonPressed && !isOnPrintScreen ? 'flex' : 'none',
                    justifyContent: 'flex-end',
                    padding: theme.spacing(3),
                }}
            >
                <Button variant='contained' onClick={() => setIsExportButtonPressed(true)}>
                    Exportovat do PDF
                </Button>
            </div>

            <div
                id='pdf'
                style={{
                    backgroundColor: theme.palette.customBackground.default,
                    margin: 'auto',
                    padding: !isExportButtonPressed && !isOnPrintScreen ? theme.spacing(3) : 0,
                    width: !isExportButtonPressed && !isOnPrintScreen ? '60%' : '100%',
                }}
            >
                <Stack justifyContent='space-between' direction='row'>
                    <div style={{ width: '150px', height: '150px' }}>
                        <img src={HappyTimesLogo} alt='Happytimes logo' style={{ width: '100%', height: '100%' }} id='logo' />
                    </div>
                    <div style={{ minWidth: '280px', marginBottom: theme.spacing(5) }}>
                        <Typography variant='h5' textAlign='end' my={3}>
                            Faktura
                        </Typography>
                        <Stack justifyContent='space-between' direction='row'>
                            <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
                                ČÍSLO FAKTURY:
                            </Typography>
                            <Typography variant='body2'>{invoice.invoiceNumber}</Typography>
                        </Stack>
                        <Stack justifyContent='space-between' direction='row'>
                            <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
                                DATUM VYSTAVENÍ:
                            </Typography>
                            <Typography variant='body2'>{new Date(invoice.issueDate).toLocaleDateString('cs')}</Typography>
                        </Stack>
                        <Stack justifyContent='space-between' direction='row'>
                            <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
                                DATUM ZDAN. PLNĚNÍ:
                            </Typography>
                            <Typography variant='body2'>{new Date(invoice.taxDate).toLocaleDateString('cs')}</Typography>
                        </Stack>
                        <Stack justifyContent='space-between' direction='row'>
                            <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
                                DATUM SPLATNOSTI:
                            </Typography>
                            <Typography variant='body2'>{new Date(invoice.dueDate).toLocaleDateString('cs')}</Typography>
                        </Stack>
                        {invoice.orderNumber && (
                            <Stack justifyContent='space-between' direction='row'>
                                <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
                                    ČÍSLO OBJEDNÁVKY:
                                </Typography>
                                <Typography variant='body2'>{invoice.orderNumber}</Typography>
                            </Stack>
                        )}
                    </div>
                </Stack>

                <Grid container mb={5}>
                    <Grid item xs={6}>
                        <Typography
                            variant='h6'
                            color='primary'
                            borderBottom='2px solid'
                            width='85%'
                            mb={3}
                            style={{ fontWeight: 'bold' }}
                        >
                            DODAVATEL
                        </Typography>
                        <Typography variant='body2'>{invoice.invoiceIssuer?.name ?? ''}</Typography>
                        <Typography variant='body2'>{invoice.invoiceIssuer?.address ?? ''}</Typography>
                        {invoice.invoiceIssuer?.companyRegistrationNumber && (
                            <Typography variant='body2'>IČ: {invoice.invoiceIssuer.companyRegistrationNumber}</Typography>
                        )}
                        {invoice.invoiceIssuer?.valueAddedTaxIdentificationNumber && (
                            <Typography variant='body2'>
                                DIČ: {invoice.invoiceIssuer.valueAddedTaxIdentificationNumber}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant='h6' color='primary' borderBottom='2px solid' mb={3} style={{ fontWeight: 'bold' }}>
                            ODBĚRATEL
                        </Typography>
                        <Typography variant='body2'>{invoice.client?.name ?? ''}</Typography>
                        <Typography variant='body2'>
                            {invoice.client?.address ? `${invoice.client.address.city ?? ''} ${invoice.client.address.zip ?? ''} ${invoice.client.address.street ?? ''
                                }` : ''}
                        </Typography>
                        {invoice.client?.companyRegistrationNumber && (
                            <Typography variant='body2'>IČ: {invoice.client.companyRegistrationNumber}</Typography>
                        )}
                        {invoice.client?.valueAddedTaxIdentificationNumber && (
                            <Typography variant='body2'>DIČ: {invoice.client.valueAddedTaxIdentificationNumber}</Typography>
                        )}
                    </Grid>
                </Grid>

                <Grid container>
                    <Grid item xs={6} mb={2}>
                        <Typography
                            variant='h6'
                            borderBottom='2px solid'
                            style={{
                                borderBottomColor: theme.palette.primary.main,
                                fontWeight: 'bold',
                            }}
                        >
                            PRODUKT
                        </Typography>
                    </Grid>
                    {invoice.invoiceIssuer?.valueAddedTaxIdentificationNumber ? (
                        <Grid item xs={2} mb={2}>
                            <Typography
                                variant='h6'
                                borderBottom='2px solid'
                                style={{
                                    borderBottomColor: theme.palette.primary.main,
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                }}
                            >
                                DPH
                            </Typography>
                        </Grid>
                    ) : (
                        <Grid
                            item
                            xs={2}
                            mb={2}
                            borderBottom='2px solid'
                            style={{
                                borderBottomColor: theme.palette.primary.main,
                                fontWeight: 'bold',
                                textAlign: 'center',
                            }}
                        />
                    )}

                    <Grid item xs={2} mb={2}>
                        <Typography
                            variant='h6'
                            borderBottom='2px solid'
                            style={{
                                borderBottomColor: theme.palette.primary.main,
                                fontWeight: 'bold',
                                textAlign: 'center',
                            }}
                        >
                            CENA
                        </Typography>
                    </Grid>
                    <Grid item xs={2} mb={2}>
                        <Typography
                            variant='h6'
                            borderBottom='2px solid'
                            style={{
                                borderBottomColor: theme.palette.primary.main,
                                fontWeight: 'bold',
                                textAlign: 'center',
                            }}
                        >
                            CELKEM
                        </Typography>
                    </Grid>

                    <Grid
                        item
                        xs={invoice.invoiceIssuer?.valueAddedTaxIdentificationNumber ? 6 : 8}
                        bgcolor={theme.palette.background.default}
                        p={2}
                        border='1px solid'
                        borderColor='white'
                    >
                        <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                            {invoice.productsText ?? ''}
                        </Typography>
                    </Grid>
                    {invoice.invoiceIssuer?.valueAddedTaxIdentificationNumber && (
                        <Grid item xs={2} bgcolor={theme.palette.background.default} p={2} border='1px solid' borderColor='white'>
                            <Typography variant='body2' style={{ textAlign: 'center' }}>
                                21%
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={2} bgcolor={theme.palette.background.default} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' style={{ textAlign: 'center' }}>
                            {getNumberRoundedInFormattedString(invoice.invoicedWithoutTax)}
                        </Typography>
                    </Grid>
                    <Grid item xs={2} bgcolor={theme.palette.background.default} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' style={{ textAlign: 'center' }}>
                            {getNumberRoundedInFormattedString(invoice.invoicedWithoutTax)}
                        </Typography>
                    </Grid>

                    <Grid item xs={6} />
                    <Grid item xs={2} />
                    <Grid item xs={2} bgcolor={theme.palette.background.default} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                            Součet
                        </Typography>
                    </Grid>
                    <Grid item xs={2} bgcolor={theme.palette.background.default} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' style={{ fontWeight: 'bold', textAlign: 'center' }}>
                            {getNumberRoundedInFormattedString(invoice.invoicedWithoutTax)}
                        </Typography>
                    </Grid>

                    <Grid item xs={6} />
                    <Grid item xs={2} />
                    {invoice.invoiceIssuer.valueAddedTaxIdentificationNumber && (
                        <>
                            <Grid
                                item
                                xs={2}
                                bgcolor={theme.palette.background.default}
                                p={2}
                                border='1px solid'
                                borderColor='white'
                            >
                                <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                                    DPH 21%
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={2}
                                bgcolor={theme.palette.background.default}
                                p={2}
                                border='1px solid'
                                borderColor='white'
                            >
                                <Typography variant='body2' style={{ fontWeight: 'bold', textAlign: 'center' }}>
                                    {getNumberRoundedInFormattedString(invoice.invoicedWithoutTax * 0.21)}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={6} />
                    <Grid item xs={2} />
                    <Grid item xs={2} bgcolor={theme.palette.primary.main} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' color='white' style={{ fontWeight: 'bold' }}>
                            K úhradě
                        </Typography>
                    </Grid>
                    <Grid item xs={2} bgcolor={theme.palette.primary.main} p={2} border='1px solid' borderColor='white'>
                        <Typography variant='body2' color='white' style={{ fontWeight: 'bold', textAlign: 'center' }}>
                            {invoice.invoiceIssuer.valueAddedTaxIdentificationNumber
                                ? getNumberRoundedInFormattedString(invoice.invoicedWithoutTax + invoice.invoicedWithoutTax * 0.21)
                                : getNumberRoundedInFormattedString(invoice.invoicedWithoutTax)}
                        </Typography>
                    </Grid>
                </Grid>

                <Typography
                    variant='h6'
                    borderBottom='2px solid'
                    style={{
                        borderBottomColor: theme.palette.primary.main,
                        fontWeight: 'bold',
                    }}
                    my={3}
                >
                    PLATEBNÍ ÚDAJE
                </Typography>
                <Stack direction='row' justifyContent='space-between'>
                    <Stack width='40%' direction='row' justifyContent='space-between'>
                        <Stack>
                            {isInvoiceTypeSelected && (
                                <Typography variant='body2' color='primary'>
                                    Způsob platby:
                                </Typography>
                            )}
                            <Typography variant='body2' color='primary'>
                                Datum splatnosti:
                            </Typography>
                            {invoice.invoiceIssuer.bankAccount && (
                                <Typography variant='body2' color='primary'>
                                    Bankovní účet:
                                </Typography>
                            )}
                            {invoice.invoiceNumber && (
                                <Typography variant='body2' color='primary'>
                                    Variabilní symbol:
                                </Typography>
                            )}
                            {invoice.invoiceIssuer.iban && (
                                <Typography variant='body2' color='primary'>
                                    IBAN:
                                </Typography>
                            )}
                            {invoice.invoiceIssuer.swift && (
                                <Typography variant='body2' color='primary'>
                                    SWIFT:
                                </Typography>
                            )}
                        </Stack>

                        <Stack>
                            {isInvoiceTypeSelected && (
                                <Typography variant='body2'>
                                    {convertInvoicePaymentTypeToString(invoice.paymentType)}
                                </Typography>
                            )}

                            <Typography variant='body2'>{new Date(invoice.dueDate).toLocaleDateString('cs')}</Typography>

                            {invoice.invoiceIssuer.bankAccount && (
                                <Typography variant='body2'>{invoice.invoiceIssuer.bankAccount}</Typography>
                            )}

                            {invoice.invoiceNumber && <Typography variant='body2'>{invoice.invoiceNumber}</Typography>}

                            {invoice.invoiceIssuer.iban && <Typography variant='body2'>{invoice.invoiceIssuer.iban}</Typography>}

                            {invoice.invoiceIssuer.swift && (
                                <Typography variant='body2'>{invoice.invoiceIssuer.swift}</Typography>
                            )}
                        </Stack>
                    </Stack>

                    {invoice.invoiceIssuer.imageData && (
                        <img
                            style={{
                                maxWidth: '48%',
                            }}
                            src={`data:image/png;base64,${invoice.invoiceIssuer.imageData}`}
                            alt={invoice.invoiceIssuer.imageName}
                        />
                    )}
                </Stack>
            </div>
        </div>
    )
}

export default InvoiceDetail
